import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

const GREETING_STORAGE_KEY = 'careerfy-daily-greeting';

interface GreetingState {
  lastShownDate: string;
  userId: string;
}

export function useDailyGreeting() {
  const { user } = useAuth();
  const [shouldShowGreeting, setShouldShowGreeting] = useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);

  useEffect(() => {
    if (!user?.id) {
      setShouldShowGreeting(false);
      setIsCheckingAdmin(false);
      return;
    }

    // Check if user is an institution admin - if so, skip greeting
    const checkAdminAndGreeting = async () => {
      setIsCheckingAdmin(true);
      
      try {
        const { data: adminRows } = await supabase
          .from('institution_admins')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);

        // Skip greeting for TPO/institution admins
        if (adminRows && adminRows.length > 0) {
          setShouldShowGreeting(false);
          setIsCheckingAdmin(false);
          return;
        }

        const today = new Date().toDateString();
        
        const stored = localStorage.getItem(GREETING_STORAGE_KEY);
        
        if (stored) {
          const state: GreetingState = JSON.parse(stored);
          
          // Only show if it's a new day for this user
          if (state.userId === user.id && state.lastShownDate === today) {
            setShouldShowGreeting(false);
            setIsCheckingAdmin(false);
            return;
          }
        }
        
        // Show greeting for first login of the day
        setShouldShowGreeting(true);
      } catch (error) {
        console.error('Error checking admin status or greeting state:', error);
        setShouldShowGreeting(true);
      } finally {
        setIsCheckingAdmin(false);
      }
    };

    checkAdminAndGreeting();
  }, [user?.id]);

  const dismissGreeting = () => {
    if (!user?.id) return;
    
    const today = new Date().toDateString();
    const state: GreetingState = {
      lastShownDate: today,
      userId: user.id,
    };
    
    try {
      localStorage.setItem(GREETING_STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving greeting state:', error);
    }
    
    setShouldShowGreeting(false);
  };

  return {
    shouldShowGreeting: !isCheckingAdmin && shouldShowGreeting,
    dismissGreeting,
  };
}
